import {
  Box,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
} from '@chakra-ui/react';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWidget } from 'widgets/sdk';

import { DATA_TABLE_MODAL_ID, WIDGET_RENDER_DELAY } from './DataTableChartCommon.config';
import { fitWidgetToContainer } from './DataTableChartCommon.helpers';
import {
  DataTableExpandModalContentProps,
  DataTableExpandModalProps,
  RenderWidgetComponentProps,
} from './DataTableExpandModal.types';

export const DataTableExpandModal = memo(({ isOpen, onClose, getWidget, component }: DataTableExpandModalProps) => {
  const [isWidgetLoaded, setIsWidgetLoaded] = useState<boolean>(false);
  const { widgetId, widthPx, heightPx } = useWidget();

  const handleClose = () => {
    setIsWidgetLoaded(false);
    onClose();
  };

  return (
    <Modal
      size={'xl'}
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      closeOnEsc
      closeOnOverlayClick
      returnFocusOnClose={false}
    >
      <ModalOverlay />
      <DataTableExpandModalContent
        isWidgetLoaded={isWidgetLoaded}
        setIsWidgetLoaded={setIsWidgetLoaded}
        widgetId={widgetId}
        widthPx={widthPx}
        heightPx={heightPx}
        getWidget={getWidget}
        component={component}
      />
    </Modal>
  );
});

DataTableExpandModal.displayName = 'DataTableExpandModal';

const DataTableExpandModalContent = memo(
  ({
    isWidgetLoaded,
    setIsWidgetLoaded,
    widgetId,
    getWidget,
    component,
    widthPx,
    heightPx,
  }: DataTableExpandModalContentProps) => {
    const { t } = useTranslation('editor_side_menu_settings');

    return (
      <ModalContent minW={{ base: 'auto', lg: '72.5rem' }} maxH={{ base: 'auto', lg: '40rem' }}>
        <ModalHeader>{t('chartSettings.editDataTable')}</ModalHeader>
        <ModalCloseButton
          position={'absolute'}
          background={'white'}
          borderRadius={'9999px'}
          right={'-2.5rem'}
          top={'0'}
          _hover={{ bg: 'white-alpha.500' }}
          data-testid={'datatable-close-button'}
        />
        <ModalBody padding={'unset'}>
          <Divider />
          <Flex w={'100%'} p={4} gap={4} direction={['column', 'column', 'column', 'row']}>
            <Flex>
              {/* Use skeleton to delay preview as resizing takes a few mseconds after modal is rendered */}
              <Skeleton
                id={DATA_TABLE_MODAL_ID}
                isLoaded={isWidgetLoaded}
                width='100%'
                height='300px'
                rounded={'lg'}
                position={'relative'}
                alignItems={'center'}
                justifyContent={'center'}
              >
                <Box
                  display={'flex'}
                  position={'relative'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  minW={'sm'}
                  borderWidth='1px'
                  borderRadius='lg'
                  overflow='hidden'
                  p={2}
                  bg='white'
                  w={'100%'}
                  h={'100%'}
                >
                  <RenderWidgetComponent
                    widgetId={widgetId}
                    getWidget={getWidget}
                    setIsWidgetLoaded={setIsWidgetLoaded}
                    widthPx={widthPx}
                    heightPx={heightPx}
                  />
                </Box>
              </Skeleton>
            </Flex>
            <Flex w={'100%'} h={{ base: '300px', lg: '500px' }} overflow={'auto'} position='relative'>
              <Skeleton isLoaded={isWidgetLoaded} width={'full'} height={'full'} rounded={'lg'}>
                {component}
              </Skeleton>
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
    );
  },
);
DataTableExpandModalContent.displayName = 'DataTableExpandModalContent';

const RenderWidgetComponent = ({
  setIsWidgetLoaded,
  widthPx,
  heightPx,
  widgetId,
  getWidget,
}: RenderWidgetComponentProps) => {
  const Widget = getWidget();
  const [customWidgetData, setCustomWidgetData] = useState({
    leftPx: 0,
    topPx: 0,
    widthPx: widthPx,
    heightPx: heightPx,
  });

  const updateWidgetData = useCallback(() => {
    const elemEl: HTMLElement = document.getElementById(DATA_TABLE_MODAL_ID)!!;
    if (elemEl) {
      const widgetDimension = fitWidgetToContainer({ widthPx, heightPx }, elemEl);
      if (widgetDimension) {
        // position based on container height/width and widget resized width and height
        const widgetResizeHeight = elemEl.offsetHeight;
        const widgetResizeWidtth = elemEl.offsetWidth;
        const topPreviewPx = (widgetResizeHeight - widgetDimension?.height) / 2;
        const leftPreviewPx = (widgetResizeWidtth - widgetDimension?.width) / 2;
        setCustomWidgetData((prevData) => ({
          ...prevData,
          leftPx: leftPreviewPx || prevData.leftPx,
          topPx: topPreviewPx || prevData.topPx,
          widthPx: widgetDimension?.width || prevData.widthPx,
          heightPx: widgetDimension?.height || prevData.heightPx,
        }));
        // render skeletion to prevent chart jumpyness when resizing
        setTimeout(() => setIsWidgetLoaded(true), WIDGET_RENDER_DELAY);
      }
    }
  }, [setIsWidgetLoaded, widthPx, heightPx]);

  // useEffect hook to run the updateWidgetData function on component mount and whenever updateWidgetData changes.
  useEffect(() => {
    updateWidgetData();
  }, [updateWidgetData]);

  return <Widget widgetId={widgetId} isReadOnly customWidgetData={customWidgetData} />;
};
